import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import './ServerSetup.css';

function ServerSetup({ token, onServerCreated, onLogout }) {
  const [view, setView] = useState('choice'); // 'choice', 'create', 'join'
  const [serverName, setServerName] = useState('');
  const [availableServers, setAvailableServers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (view === 'join') {
      fetchAvailableServers();
    }
  }, [view]);

  const fetchAvailableServers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/servers/discover`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('All servers:', response.data);
      const nonMemberServers = response.data.filter(s => !s.isMember);
      console.log('Non-member servers:', nonMemberServers);
      setAvailableServers(nonMemberServers);
    } catch (err) {
      console.error('Error fetching servers:', err);
      setError('Failed to load servers');
    }
  };

  const handleCreateServer = async (e) => {
    e.preventDefault();
    if (!serverName.trim()) {
      setError('Please enter a server name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${API_URL}/api/servers/create`,
        { name: serverName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onServerCreated(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create server');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinServer = async (serverId) => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${API_URL}/api/servers/join`,
        { serverId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onServerCreated(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to join server');
    } finally {
      setLoading(false);
    }
  };

  if (view === 'choice') {
    return (
      <div className="server-setup">
        <button className="logout-button-absolute" onClick={onLogout} title="Logout (Clear Session)">
          🚪 Logout
        </button>
        <div className="server-setup-container">
          <h1>Welcome to Ripkord!</h1>
          <p>Get started by creating a server or joining an existing one</p>

          <div className="server-setup-options">
            <button
              className="server-setup-button create"
              onClick={() => setView('create')}
            >
              <div className="button-icon">+</div>
              <div className="button-content">
                <h3>Create a Server</h3>
                <p>Start your own community</p>
              </div>
            </button>

            <button
              className="server-setup-button join"
              onClick={() => setView('join')}
            >
              <div className="button-icon">🔍</div>
              <div className="button-content">
                <h3>Join a Server</h3>
                <p>Explore existing communities</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'create') {
    return (
      <div className="server-setup">
        <button className="logout-button-absolute" onClick={onLogout} title="Logout (Clear Session)">
          🚪 Logout
        </button>
        <div className="server-setup-container">
          <button className="back-button" onClick={() => setView('choice')}>
            ← Back
          </button>

          <h1>Create Your Server</h1>
          <p>Give your server a personality with a name. You can always change it later.</p>

          <form onSubmit={handleCreateServer} className="server-setup-form">
            <div className="form-group">
              <label>SERVER NAME</label>
              <input
                type="text"
                value={serverName}
                onChange={(e) => setServerName(e.target.value)}
                placeholder="My Awesome Server"
                maxLength={50}
                disabled={loading}
                autoFocus
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="form-actions">
              <button
                type="button"
                className="secondary-button"
                onClick={() => setView('choice')}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="primary-button"
                disabled={loading || !serverName.trim()}
              >
                {loading ? 'Creating...' : 'Create Server'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (view === 'join') {
    return (
      <div className="server-setup">
        <button className="logout-button-absolute" onClick={onLogout} title="Logout (Clear Session)">
          🚪 Logout
        </button>
        <div className="server-setup-container">
          <button className="back-button" onClick={() => setView('choice')}>
            ← Back
          </button>

          <h1>Join a Server</h1>
          <p>Browse available servers and join one to start chatting</p>

          {error && <div className="error-message">{error}</div>}

          <div className="available-servers-list">
            {availableServers.length === 0 ? (
              <div className="no-servers">
                <p>No servers available to join right now.</p>
                <p>Why not create your own?</p>
                <button
                  className="primary-button"
                  onClick={() => setView('create')}
                >
                  Create Server
                </button>
              </div>
            ) : (
              availableServers.map(server => (
                <div key={server.id} className="server-item">
                  <div className="server-info">
                    <h3>{server.name}</h3>
                    <p>{server.memberCount} member{server.memberCount !== 1 ? 's' : ''}</p>
                  </div>
                  <button
                    className="join-button"
                    onClick={() => handleJoinServer(server.id)}
                    disabled={loading}
                  >
                    Join
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default ServerSetup;
